import { supabase } from "@/services/supabase/createClient";
import { v2 } from "@pufflig/prompt-studio-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash.isequal";
import { useCallback } from "react";

const getPromptbook = async (promptbookId: string) => {
  const { data, error } = await supabase.rpc("get_promptbook", { promptbook_uuid: promptbookId });

  if (error) {
    throw error;
  }

  return data?.[0];
};

export const usePromptbook = (promptbookId: string) => {
  const res = useQuery({
    queryKey: ["promptbooks", promptbookId],
    queryFn: () => getPromptbook(promptbookId),
    refetchOnWindowFocus: false,
  });

  return {
    promptbook: res.data,
    isLoadingPromptbook: res.isLoading,
    errorLoadingPromptbook: res.error,
  };
};

export const updatePromptbookState = async (promptbookId: string, state: v2.BasePromptbookState) => {
  const sanitizedState = { ...state, externalResources: {} } as v2.BasePromptbookState;

  await supabase
    .from("promptbooks")
    .update({ editor_state: sanitizedState as any })
    .eq("resource_id", promptbookId);
};

export const useUpdatePromptbookState = () => {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({ promptbookId, state }: { promptbookId: string; state: any }) =>
      updatePromptbookState(promptbookId, state),

    onSuccess: (_, { promptbookId, state }) => {
      const sanitizedState = { ...state, externalResources: {} };
      queryClient.setQueryData(["promptbooks", promptbookId], (old: any) => ({
        ...old,
        editor_state: sanitizedState,
      }));
    },
  });

  /**
   * Only hit the API when `state` has *actually* changed compared
   * with the last value we have in the cache.
   */
  return useCallback(
    async (data: { promptbookId: string; state: any }) => {
      const { promptbookId, state } = data;
      const sanitizedState = { ...state, externalResources: {} };

      const cached = queryClient.getQueryData(["promptbooks", promptbookId]) as { editor_state?: any } | undefined;

      if (cached && isEqual(cached.editor_state, sanitizedState)) return;

      await mutateAsync({ promptbookId, state: sanitizedState });
    },
    [queryClient, mutateAsync],
  );
};
